import React, { useEffect, useState } from "react";
import { Button, Input, Row, Col, Form, Checkbox, Typography } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./index.css";
import RemitClickInfo from "../Components/RemitClickInfo";
import { CloseOutlined } from "@ant-design/icons";
import logo from "../Image/logo.png";
import { signIn, resendSignUpCode } from "aws-amplify/auth";

const REMEMBERED_USERNAME_KEY = "bajcan_remembered_username";
const OTP_RECOVERY_PASSWORD_KEY = "bajcan_otp_recovery_password";

const getErrorMessage = (error, fallbackMessage) => {
  const rawMessage =
    error?.message ||
    error?.errors?.[0]?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "";
  const message = String(rawMessage || error || "")
    .split(":")
    .pop()
    .trim();
  return message || fallbackMessage;
};

const normalizeUsername = (value) => String(value || "").trim().toLowerCase();

const SignIn = ({ onLoginSuccess }) => {
  const { Text } = Typography;
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isError, setIsError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const isMobile = viewportWidth < 768;

  const redirectToOtpAfterSendingCode = async (username, password) => {
    sessionStorage.setItem(OTP_RECOVERY_PASSWORD_KEY, password);
    await resendSignUpCode({ username });
    navigate("/signUp", {
      state: {
        verifyOtp: true,
        username,
        otpAlreadySent: true,
      },
    });
  };

  const handleSignIn = async ({ username, password, remember }) => {
    if (isSigningIn) {
      return;
    }

    const normalizedUsername = normalizeUsername(username);

    try {
      setIsSigningIn(true);
      setIsError(false);
      setErrMsg("");

      const { isSignedIn, nextStep } = await signIn({
        username: normalizedUsername,
        password,
      });

      if (
        !isSignedIn &&
        (nextStep?.signInStep === "CONFIRM_SIGN_UP" ||
          nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
          nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE")
      ) {
        await redirectToOtpAfterSendingCode(normalizedUsername, password);
        return;
      }

      if (!isSignedIn) {
        setIsError(true);
        setErrMsg(
          "Sign in could not be completed. Please verify your account.",
        );
        return;
      }

      if (remember) {
        localStorage.setItem(REMEMBERED_USERNAME_KEY, normalizedUsername);
      } else {
        localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      }

      onLoginSuccess({ shouldShowVerificationPrompt: true });
    } catch (error) {
      if (error?.name === "UserNotConfirmedException") {
        try {
          await redirectToOtpAfterSendingCode(normalizedUsername, password);
        } catch (resendError) {
          setIsError(true);
          setErrMsg(
            getErrorMessage(
              resendError,
              "Unable to send a verification code right now.",
            ),
          );
        }
        return;
      }

      console.error("Error signing in:", error);
      setIsError(true);
      setErrMsg(
        getErrorMessage(error, "Unable to sign in right now. Please try again."),
      );
    } finally {
      setIsSigningIn(false);
    }
  };

  useEffect(() => {
    const handleWindowResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    const prefillUsername = normalizeUsername(location.state?.prefillUsername);
    if (!prefillUsername) {
      return;
    }

    form.setFieldsValue({ username: prefillUsername });
  }, [form, location.state]);

  const renderSignInForm = () => (
    <div className="form">
      <Form
        form={form}
        initialValues={{
          username: localStorage.getItem(REMEMBERED_USERNAME_KEY) || "",
          remember: !!localStorage.getItem(REMEMBERED_USERNAME_KEY),
        }}
        onValuesChange={() => {
          if (isError) {
            setIsError(false);
            setErrMsg("");
          }
        }}
        onFinish={handleSignIn}
      >
        <label className="formlabel" style={{ fontSize: "16px" }}>
          Username
        </label>
        <Form.Item
          name="username"
          rules={[
            {
              required: true,
              message: "Please enter Username",
            },
          ]}
        >
          <Input className="forminput" placeholder="Username" />
        </Form.Item>

        <label className="formlabel" style={{ fontSize: "16px" }}>
          Password
        </label>
        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: "Please input your password!",
            },
          ]}
        >
          <Input.Password className="forminput" placeholder="Password" />
        </Form.Item>

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Remember me</Checkbox>
            </Form.Item>
            <Link to="/forgetPassword">
              <label>Forgot Password?</label>
            </Link>
          </div>
        </Form.Item>

        {isError ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1rem",
            }}
          >
            <Text style={{ color: "red" }}>{errMsg}</Text>
          </div>
        ) : null}

        <Button
          htmlType="submit"
          className="signupbtn"
          loading={isSigningIn}
          disabled={isSigningIn}
        >
          Login
        </Button>
      </Form>

      <p className="signupFooter">
        Don't have an account?
        <Link to="/signUp" style={{ textDecoration: "none" }}>
          Sign Up
        </Link>
      </p>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <div className="signin-mobile-wrap">
          <Row
            className="signin-mobile-header"
            style={{
              display: "flex",
              borderBottomStyle: "solid",
              borderBottomColor: "#F2F2F2",
              borderBottomWidth: "0.1px",
              flexShrink: 0,
            }}
          >
            <Col
              span={20}
              style={{
                display: "flex",
                paddingTop: 10,
                paddingBottom: 10,
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "1rem",
                }}
              >
                <img
                  src={logo}
                  className="headerLogo headerLogo--mobile"
                  alt="BajCan"
                />
              </div>
            </Col>
            <Col
              span={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Link to="/">
                <Button
                  type="primary"
                  icon={<CloseOutlined />}
                  className="crossSign-mobile"
                />
              </Link>
            </Col>
          </Row>
          <Row
            className="signin-row--mobile"
            style={{
              flex: 1,
              minHeight: 0,
              height: "auto",
            }}
          >
            <Col xs={{ span: 24 }}>
              <div className="signinRightSec">
                <div className="signin-mobile-welcome">
                  <p className="signin-mobile-tagline">Welcome back</p>
                  <p className="signin-mobile-subtitle">
                    Sign in to manage your transfers securely
                  </p>
                </div>
                <div className="signbox signbox--mobile-signin">
                  <h2 className="signupTitle">Sign In</h2>
                  {renderSignInForm()}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      ) : (
        <Row
          style={{
            height: "100vh",
          }}
        >
          <Col xs={{ span: 0 }} md={{ span: 12 }} lg={{ span: 10 }}>
            <RemitClickInfo />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 14 }}>
            <div className="signinRightSec">
              <div className="signbox">
                <Link
                  to="/"
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<CloseOutlined />}
                    className="crossSign"
                  />
                </Link>
                <h2 className="signupTitle">Sign In</h2>
                {renderSignInForm()}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SignIn;
